import Actions from "../actions";
import { NotificationsLoadedAction, SelectedBranchChangedAction, IncomingMessagesSummaryLoadedAction, IncomingMessageReceivedAction, CommunicationMarkedAsReadAction, TasksUpdatedAction, SelectedCashRegisterNameChangedAction, UnreadMessagesMarkedAsReadAction, CommunicationQuotasLoadedAction } from "./types";
import { Notification, IncomingMessageNotification, IncomingMessagesSummary, CommunicationQuotas } from "../../models";
import { AppThunkAction } from "..";
import { getNotificationsAsync, getIncomingMessagesSummaryAsync, markAllCommunicationsAsReadAsync, markCommunicationAsReadAsync, getAdminCommunicationQuotasAsync } from "../../ajax/requests";
import { alertError } from "../alerts/actions";
import { translate } from "../translations/hooks";
import { TK } from "../translations/translationKeys";
import { getCommunicationQuotas as getCommunicationQuotasSelector, getCommunicationQuotasFetchedAt } from "./selectors";

const notificationsLoaded = (notifications: Notification[]): NotificationsLoadedAction => ({
    type: Actions.NotificationsLoaded,
    payload: notifications,
});

export const tasksUpdated = (): TasksUpdatedAction => ({
    type: Actions.TasksUpdated,
    payload: {
        updatedAt: Date.now(),
    },
});


export const loadNotifications = () : AppThunkAction<Promise<void>> => {
    return async (dispatch) => {
        try {
            const notifications = await getNotificationsAsync();
            dispatch(notificationsLoaded(notifications));
        }
        catch(_) {
        }
    };
};

export const selectedBranchChange = (branch: string): SelectedBranchChangedAction => ({
    type: Actions.SelectedBranchChanged,
    payload: {branch},
});

const incomingMessagesSummaryLoaded = (summary: IncomingMessagesSummary): IncomingMessagesSummaryLoadedAction => ({
    type: Actions.IncomingMessagesSummaryLoaded,
    payload: summary,
});

const communicationQuotasLoaded = (communicationQuotas: CommunicationQuotas, fetchedAt: number): CommunicationQuotasLoadedAction => ({
    type: Actions.CommunicationQuotasLoaded,
    payload: {
        communicationQuotas,
        fetchedAt,
    },
});

export const loadIncomingMessagesSummary = (): AppThunkAction<Promise<void>> => {
    return async (dispatch) => {
        try {
            const summary = await getIncomingMessagesSummaryAsync();
            dispatch(incomingMessagesSummaryLoaded(summary));
        }
        catch(error) {
            console.error('Failed loading messages summary:', error);
        }
    };
};

let communicationQuotasRequest: Promise<CommunicationQuotas | null> | null = null;
const communicationQuotasCacheTtlMs = 5 * 60 * 1000;

export const loadCommunicationQuotasIfNeeded = (): AppThunkAction<Promise<CommunicationQuotas | undefined>> => {
    return async (dispatch, getState) => {
        const existingQuotas = getCommunicationQuotasSelector(getState());
        const fetchedAt = getCommunicationQuotasFetchedAt(getState());
        const isFresh = typeof fetchedAt === 'number' && (Date.now() - fetchedAt) < communicationQuotasCacheTtlMs;

        if (existingQuotas && isFresh) {
            return existingQuotas;
        }

        if (!communicationQuotasRequest) {
            communicationQuotasRequest = getAdminCommunicationQuotasAsync()
                .then(quotas => quotas ?? null)
                .catch(error => {
                    console.error('Failed loading communication quotas:', error);
                    return null;
                })
                .finally(() => {
                    communicationQuotasRequest = null;
                });
        }

        const quotas = await communicationQuotasRequest;
        if (quotas) {
            dispatch(communicationQuotasLoaded(quotas, Date.now()));
        }

        return quotas ?? undefined;
    };
};

export const incomingMessageReceived = (message: IncomingMessageNotification): IncomingMessageReceivedAction => ({
    type: Actions.IncomingMessageReceived,
    payload: message,
});

export const communicationMarkedAsRead = (communicationId: string, read: boolean): CommunicationMarkedAsReadAction => ({
    type: Actions.CommunicationMarkedAsRead,
    payload: {communicationId, read},
});

export const unreadMessagesMarkedAsRead = (): UnreadMessagesMarkedAsReadAction => ({
    type: Actions.UnreadMessagesMarkedAsRead,
});

export const selectCashRegisterName = (name: string): SelectedCashRegisterNameChangedAction => ({
    type: Actions.SelectedCashRegisterNameChanged,
    payload: { name },
});

export const markMessageAsRead = (clientId: string, communicationId: string, read: boolean): AppThunkAction<Promise<void>> => {
    return async (dispatch) => {
        try {
            await markCommunicationAsReadAsync(clientId, communicationId, read);
            dispatch(communicationMarkedAsRead(communicationId, read));
        }
        catch(error) {
            console.error('Failed to mark message as read:', error);
        }
    };
};

export const markAllMessagesAsRead = (): AppThunkAction<Promise<void>> => {
    return async (dispatch) => {
        try {
            await markAllCommunicationsAsReadAsync();
            dispatch(unreadMessagesMarkedAsRead());
        }
        catch(error) {
            console.error('Failed to mark all messages as read:', error);
            dispatch(alertError(translate(TK.somethingWentWrong)));
        }
    };
};
